import React, { useState } from "react";
import UserProfile from "../components/Tasks/UserProfile";
import ButtonTasks from "../components/Tasks/ButtonTasks";
import History from "../components/Tasks/History";
import Loading from "../components/Loading";
import { useSelector } from "react-redux";
import { RootState, Task, TaskType } from "../redux/type";
import { Tabs, Tab } from "@mui/material";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import TaskDetailsModal from "../components/Tasks/TaskDetailsModal";
import { checkTaskApi, getInstructionByName, getTaskByName } from "../api/task";
import toast from "react-hot-toast";
import { setTasks, setUserData } from "../redux/actions";
import { useDispatch } from "react-redux";

const HomeContainer = styled.div`
  margin-top: 1.5rem;
  padding: 0 1.5rem;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;

  /* Ensure space for bottom nav */
  padding-bottom: 100px;

  /* Make scrollable if needed */
  overflow-y: auto;
  box-sizing: border-box;
`;
const StyledTabs = styled(Tabs)`
  && {
    position: relative;
    min-height: unset;
    margin-bottom: 1.5rem;

    .MuiTabs-indicator {
      background: linear-gradient(90deg, #ccff00, #3b82f6);
      height: 3px;
      border-radius: 3px;
      box-shadow: 0 2px 8px rgba(110, 231, 183, 0.4);
    }

    .MuiTabs-flexContainer {
      gap: 0.5rem;
      position: relative;
      z-index: 1;
    }

    &:after {
      content: "";
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      height: 1px;
      background: rgba(255, 255, 255, 0.1);
    }
  }
`;

const StyledTab = styled(Tab)`
  && {
    color: rgba(255, 255, 255, 0.7);
    font-weight: 600;
    font-size: 0.95rem;
    text-transform: capitalize;
    padding: 0.75rem 1rem;
    min-width: unset;
    min-height: unset;
    transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.1);
    border-radius: 8px 8px 0 0;
    z-index: 1;

    &.Mui-selected {
      color: white !important;
      background: rgba(255, 255, 255, 0.05);
    }

    &:hover {
      color: white !important;
      background: rgba(255, 255, 255, 0.05);
    }

    &:active {
      transform: scale(0.98);
    }
  }
`;
const TabContentContainer = styled.div`
  position: relative;
  min-height: 300px;
`;

const TabContent = styled(motion.div)`
  position: relative;
  width: 100%;
`;

const tabContentVariants = {
  initial: { opacity: 0, y: 10 },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.3,
      ease: [0.16, 1, 0.3, 1] as [number, number, number, number],
    },
  },
  exit: {
    opacity: 0,
    y: -10,
    transition: {
      duration: 0.2,
      ease: [0.16, 1, 0.3, 1] as [number, number, number, number],
    },
  },
};

const NoteBanner = styled(motion.div)`
  background: rgba(255, 204, 0, 0.15);
  color: #ffd700;
  padding: 0.75rem 1rem;
  border-radius: 12px;
  margin: 1rem 0 1.5rem;
  border: 1px solid rgba(255, 204, 0, 0.3);
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  backdrop-filter: blur(4px);
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: white;
  text-align: center;
  margin: 0 0 1.5rem;
  background: linear-gradient(90deg, #ccff00, #3b82f6);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  position: relative;
  padding-bottom: 0.5rem;

  &:after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 3px;
    background: linear-gradient(90deg, #ccff00, #3b82f6);
    border-radius: 3px;
  }
`;

const InstructionCard = styled(motion.div)`
  background: rgba(59, 130, 246, 0.15);
  color: #a5d8ff;
  padding: 1rem;
  border-radius: 12px;
  margin: 1rem 0 1.5rem;
  border: 1px solid rgba(59, 130, 246, 0.3);
  font-size: 0.9rem;
  position: relative;
  backdrop-filter: blur(4px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const InstructionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
`;

const InstructionTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: #3b82f6;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const InstructionIcon = styled.span`
  font-size: 1.2rem;
`;

const InstructionContent = styled.div`
  line-height: 1.5;
  ul {
    padding-left: 1.2rem;
    margin: 0.5rem 0;
  }
  li {
    margin-bottom: 0.3rem;
  }
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.5);
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    color: white;
    background: rgba(255, 255, 255, 0.1);
  }
`;

const ChipsContainer = styled.div`
  display: flex;
  overflow-x: auto;
  gap: 0.5rem;
  padding-bottom: 0.5rem;
  margin-bottom: 1rem;
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.2) transparent;

  &::-webkit-scrollbar {
    height: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: rgba(255, 255, 255, 0.2);
    border-radius: 3px;
  }
`;

const Chip = styled.div<{ active: boolean }>`
  padding: 0.4rem 0.8rem;
  border-radius: 999px;
  background: ${({ active }) =>
    active ? "rgba(255,255,255,0.2)" : "rgba(255,255,255,0.08)"};
  color: white;
  font-weight: 500;
  font-size: 0.9rem;
  cursor: pointer;
  border: ${({ active }) =>
    active ? "1px solid #ccff00" : "1px solid transparent"};
  transition: all 0.2s ease-in-out;
  white-space: nowrap;
  flex-shrink: 0;

  &:hover {
    background: rgba(255, 255, 255, 0.15);
  }
`;

const TaskPage = () => {
  const user = useSelector((state: RootState) => state.userData);
  const [activeTab, setActiveTab] = useState<"tasks" | "history">("tasks");
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [roleFilter, setRoleFilter] = useState<any>("all");

  const dispatch = useDispatch();

  const validateTask = async (task: Task, content: string) => {
    try {
      const result = await checkTaskApi(user.uid, task._id, content);

      if (!result) {
        toast.error("Error validating task");
        return false;
      }

      if (result.error) {
        toast.error(result.error);
        return false;
      }

      dispatch(setUserData(result.user));
      dispatch(setTasks(result.tasks));

      toast.success("Task validated successfully");
      setSelectedTask(null);
      return result;
    } catch (error) {
      toast.error(error.message || "Error validating task");
      console.error("Error validating task:", error);
      return false;
    }
  };

  const roleChips = () => {
    const base = [{ label: "All", value: "all" }];
    const types = [
      TaskType.LOREKEEPER,
      TaskType.SIGNAL_BOOSTER,
      TaskType.FEED_WATCHER,
      TaskType.DECODER,
      TaskType.NETWORKING_EVENT,
      TaskType.UI_UX_ARCHITECT,
      TaskType.INTERFACE_ARCHITECT,
      TaskType.SYSTEM_BUILDER,
    ];

    const dynamicChips = types.map((type) => ({
      label: getTaskByName(type),
      value: type,
    }));

    return [...base, ...dynamicChips];
  };

  return !user ? (
    <Loading />
  ) : (
    <HomeContainer>
      {!user.twitterHandle && (
        <NoteBanner
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          ⚠️ Please update your social handles in Account before starting social
          missions.
        </NoteBanner>
      )}

      <SectionTitle>Missions</SectionTitle>

      <StyledTabs
        value={activeTab}
        onChange={(event, newValue) => setActiveTab(newValue)}
        variant="fullWidth"
      >
        <StyledTab label="Tasks" value="tasks" />
        <StyledTab label="History" value="history" />
      </StyledTabs>

      {activeTab === "tasks" && (
        <ChipsContainer>
          {roleChips().map((chip) => (
            <Chip
              key={chip.value}
              onClick={() => setRoleFilter(chip.value)}
              active={roleFilter === chip.value}
            >
              {chip.label}
            </Chip>
          ))}
        </ChipsContainer>
      )}

      <TabContentContainer>
        <AnimatePresence mode="wait">
          <TabContent
            key={activeTab}
            variants={tabContentVariants}
            initial="initial"
            animate="animate"
            exit="exit"
          >
            {activeTab === "tasks" && (
              <ButtonTasks
                roleFilter={roleFilter}
                onTaskClick={(task) => setSelectedTask(task)}
              />
            )}

            {activeTab === "history" && <History user={user} />}
          </TabContent>
        </AnimatePresence>
      </TabContentContainer>

      {selectedTask && (
        <TaskDetailsModal
          task={selectedTask}
          onClose={() => setSelectedTask(null)}
          onValidate={async (answer) => {
            await validateTask(selectedTask, answer);
          }}
        />
      )}
    </HomeContainer>
  );
};

export default TaskPage;
